/**
 * Collaborative Document Editor Page
 *
 * Real-time collaborative editing for S3 documents using Yjs.
 * Supports multiple users editing simultaneously with WebRTC.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge, Modal } from 'flowbite-react';
import {
  HiSave,
  HiX,
  HiUserGroup,
  HiDownload,
  HiClock,
  HiCheckCircle,
  HiTemplate
} from 'react-icons/hi';
import * as Y from 'yjs';
import { WebrtcProvider } from 'y-webrtc';
import { QuillBinding } from 'y-quill';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Make_Authorized_API_Call } from '../services/api/utils';
import { API_ENDPOINTS, buildEndpoint } from '../services/api/endpoints';

// TODO: Backend endpoint /api/documents/* missing - needs to be added to template-core.yaml
// This editor requires document CRUD endpoints that don't exist yet:
// - API_ENDPOINTS.DOCUMENTS.GET - Get document content
// - API_ENDPOINTS.DOCUMENTS.UPDATE - Update document content
// Currently referencing non-existent API_ENDPOINTS.DOCUMENTS paths
import { StandardsSelector } from '../components/StandardsSelector';
import type { StandardContent } from '../services/api/standards';
import KnowledgeHarvestButton, { KnowledgeSynthesisResult } from '../components/KnowledgeHarvestButton';
import GenerateDocumentDialog, { GeneratedDocument } from '../components/GenerateDocumentDialog';
import { useAuth } from '../contexts/AuthContext';

interface DocumentContent {
  key: string;
  bucket: string;
  content: string;
  etag: string;
  contentType: string;
  lastModified: string;
  metadata: Record<string, any>;
}

const DocumentEditor: React.FC = () => {
  const { documentKey } = useParams<{ documentKey: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const ydocRef = useRef<Y.Doc | null>(null);
  const providerRef = useRef<WebrtcProvider | null>(null);

  const [document, setDocument] = useState<DocumentContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [connectedUsers, setConnectedUsers] = useState<number>(0);
  const [isDirty, setIsDirty] = useState(false);
  const [showStandardsModal, setShowStandardsModal] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState<StandardContent | null>(null);
  const [synthesisResult, setSynthesisResult] = useState<KnowledgeSynthesisResult | null>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  // Decode the document key from URL
  const decodedKey = decodeURIComponent(documentKey || '');

  // Fetch document content
  const fetchDocument = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await Make_Authorized_API_Call<DocumentContent>(
        API_ENDPOINTS.DOCUMENTS.GET,
        'GET',
        undefined,
        {
          params: {
            key: decodedKey,
            bucket: 'equilateral-documents-dev'
          }
        }
      );

      if (result.success && result.data) {
        setDocument(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch document');
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [decodedKey]);

  // Initialize Yjs and Quill
  useEffect(() => {
    if (!document || !editorRef.current) return;

    // Create Yjs document
    const ydoc = new Y.Doc();
    const ytext = ydoc.getText('quill');

    // Initialize Quill
    const quill = new Quill(editorRef.current, {
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ direction: 'rtl' }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean']
        ]
      },
      placeholder: 'Start typing...',
      theme: 'snow'
    });

    // Set initial content
    if (selectedStandard && selectedStandard.content) {
      // If a standard was selected, use its content
      quill.setText(selectedStandard.content);
      setSelectedStandard(null); // Clear after loading
    } else if (document.content) {
      try {
        // If content is JSON (for rich text), parse it
        const content = JSON.parse(document.content);
        quill.setContents(content);
      } catch {
        // If content is plain text, set as text
        quill.setText(document.content);
      }
    }

    // Create WebRTC provider for collaboration
    const provider = new WebrtcProvider(
      `equilateral-doc-${decodedKey}`,
      ydoc,
      {
        signaling: ['wss://signaling.yjs.dev', 'wss://y-webrtc-signaling-eu.herokuapp.com']
      }
    );

    // Set user awareness after provider is created
    const userName = user
      ? `${user.first_name} ${user.last_name}`.trim() || user.email
      : 'Anonymous User';

    provider.awareness.setLocalStateField('user', {
      name: userName,
      color: '#' + Math.floor(Math.random() * 16777215).toString(16)
    });

    // Bind Quill to Yjs
    const binding = new QuillBinding(ytext, quill, provider.awareness);

    // Track connected users
    provider.awareness.on('change', () => {
      setConnectedUsers(provider.awareness.getStates().size);
    });

    // Track document changes
    quill.on('text-change', () => {
      setIsDirty(true);
    });

    // Store refs
    quillRef.current = quill;
    ydocRef.current = ydoc;
    providerRef.current = provider;

    // Cleanup
    return () => {
      provider.destroy();
      binding.destroy();
      ydoc.destroy();
    };
  }, [document, decodedKey, selectedStandard, user]);

  // Handle standard selection
  const handleStandardSelect = useCallback((standard: StandardContent) => {
    setSelectedStandard(standard);
    setShowStandardsModal(false);

    // If we have a Quill instance, update it immediately
    if (quillRef.current) {
      quillRef.current.setText(standard.content);
      setIsDirty(true);
    }
  }, []);

  // Save document
  const saveDocument = useCallback(async () => {
    if (!quillRef.current || !document) return;

    try {
      setSaving(true);
      setError(null);

      const contents = quillRef.current.getContents();
      const contentString = JSON.stringify(contents);

      // Add metadata about selected standard if applicable
      const metadata = document.metadata || {};
      if (selectedStandard) {
        metadata.based_on_standard = selectedStandard.path;
        metadata.standard_category = selectedStandard.category;
      }

      const result = await Make_Authorized_API_Call<{ etag: string }>(
        API_ENDPOINTS.DOCUMENTS.UPDATE,
        'PUT',
        {
          key: decodedKey,
          bucket: 'equilateral-documents-dev',
          content: contentString,
          contentType: 'application/json',
          etag: document.etag, // For conflict detection
          metadata
        }
      );

      if (result.success && result.data) {
        setDocument(prev => prev ? { ...prev, etag: result.data.etag } : null);
        setLastSaved(new Date());
        setIsDirty(false);
      } else {
        throw new Error(result.message || 'Failed to save document');
      }
    } catch (err: any) {
      console.error('Error saving document:', err);
      if (err.message?.includes('modified')) {
        setError('Document has been modified by another user. Please refresh and try again.');
      } else {
        setError('Failed to save document');
      }
    } finally {
      setSaving(false);
    }
  }, [document, decodedKey, selectedStandard]);

  // Auto-save every 30 seconds if dirty
  useEffect(() => {
    if (!isDirty) return;

    const timer = setTimeout(() => {
      saveDocument();
    }, 30000);

    return () => clearTimeout(timer);
  }, [isDirty, saveDocument]);

  // Save on unmount if dirty
  useEffect(() => {
    return () => {
      if (isDirty && quillRef.current) {
        // Attempt to save before unmounting
        saveDocument();
      }
    };
  }, [isDirty, saveDocument]);

  // Keyboard shortcut for save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveDocument();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveDocument]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  // Handle knowledge harvest completion
  const handleHarvestComplete = useCallback((result: KnowledgeSynthesisResult) => {
    console.log('[DocumentEditor] Knowledge harvest complete:', result);
    setSynthesisResult(result);
    setShowGenerateDialog(true);
  }, []);

  // Handle knowledge harvest error
  const handleHarvestError = useCallback((error: string) => {
    console.error('[DocumentEditor] Knowledge harvest error:', error);
    setError(error);
  }, []);

  // Handle document generation
  const handleDocumentGenerate = useCallback(async (generatedDoc: GeneratedDocument) => {
    if (!quillRef.current) return;

    try {
      console.log('[DocumentEditor] Inserting generated document content');

      // Insert the generated content into the editor
      const currentLength = quillRef.current.getLength();
      quillRef.current.insertText(currentLength, '\n\n' + generatedDoc.content);

      // Mark as dirty so user can save
      setIsDirty(true);

      // Show success message
      setError(null);
      console.log('[DocumentEditor] Generated document inserted successfully');
    } catch (err: any) {
      console.error('[DocumentEditor] Error inserting generated document:', err);
      setError('Failed to insert generated document content');
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="xl" />
        <span className="ml-3">Loading document...</span>
      </div>
    );
  }

  if (error && !document) {
    return (
      <div className="container mx-auto p-6">
        <Alert color="failure">
          <span className="font-medium">Error!</span> {error}
          <Button
            size="sm"
            color="light"
            onClick={() => navigate(-1)}
            className="mt-4"
          >
            Go Back
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                size="sm"
                color="light"
                onClick={() => navigate(-1)}
              >
                <HiX className="w-4 h-4 mr-2" />
                Close
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {decodedKey.split('/').pop()}
                </h1>
                <p className="text-sm text-gray-500">{decodedKey}</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Connection status */}
              <div className="flex items-center">
                <HiUserGroup className="w-5 h-5 mr-2 text-gray-400" />
                <Badge color={connectedUsers > 1 ? 'green' : 'gray'}>
                  {connectedUsers} {connectedUsers === 1 ? 'user' : 'users'} editing
                </Badge>
              </div>

              {/* Save status */}
              {lastSaved && (
                <div className="flex items-center text-sm text-gray-500">
                  <HiCheckCircle className="w-4 h-4 mr-1 text-green-500" />
                  Saved {lastSaved.toLocaleTimeString()}
                </div>
              )}

              {/* Load from standard button */}
              <Button
                size="sm"
                color="light"
                onClick={() => setShowStandardsModal(true)}
              >
                <HiTemplate className="w-4 h-4 mr-2" />
                Load Standard
              </Button>

              {/* Knowledge Harvest Button */}
              <KnowledgeHarvestButton
                onHarvestComplete={handleHarvestComplete}
                onError={handleHarvestError}
                variant="secondary"
                size="sm"
                showStatus={false}
              />

              {/* Save button */}
              <Button
                size="sm"
                color="blue"
                onClick={saveDocument}
                disabled={saving || !isDirty}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <HiSave className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Error alert */}
      {error && document && (
        <div className="container mx-auto px-4 py-2">
          <Alert color="failure" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Editor */}
      <div className="container mx-auto px-4 py-6">
        <Card>
          <div className="min-h-[600px]">
            <div ref={editorRef} />
          </div>
        </Card>
      </div>

      {/* Status bar */}
      <div className="bg-white border-t px-4 py-2">
        <div className="container mx-auto flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-4">
            <span>
              {isDirty && (
                <Badge color="yellow" size="sm">
                  Unsaved changes
                </Badge>
              )}
            </span>
            {document?.metadata?.based_on_standard && (
              <span className="text-xs text-blue-600">
                Based on: {document.metadata.based_on_standard}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <span>
              Last modified: {document?.lastModified
                ? new Date(document.lastModified).toLocaleString()
                : 'Unknown'}
            </span>
            <span>Press Ctrl+S to save</span>
          </div>
        </div>
      </div>

      {/* Standards Selector Modal */}
      <Modal
        show={showStandardsModal}
        onClose={() => setShowStandardsModal(false)}
        size="4xl"
      >
        <Modal.Header>Browse Standards</Modal.Header>
        <Modal.Body>
          <StandardsSelector
            onSelect={handleStandardSelect}
            onCancel={() => setShowStandardsModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* Generate Document Dialog */}
      {synthesisResult && (
        <GenerateDocumentDialog
          isOpen={showGenerateDialog}
          onClose={() => setShowGenerateDialog(false)}
          patterns={synthesisResult.patterns}
          gaps={synthesisResult.gaps}
          insights={synthesisResult.insights}
          onGenerate={handleDocumentGenerate}
        />
      )}
    </div>
  );
};

export default DocumentEditor;